// skipcq: JS-W1028, JS-0128
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import DialogContentText from '@mui/material/DialogContentText';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';

/**
 * SearchResultsList
 * Displays a list of search results with "Use This" buttons for selecting URLs to scrape.
 */
function SearchResultsList({
  searchResults,
  scrapingLoading,
  scrapingUrl,
  onSelectClick,
  onLinkClick,
}) {
  if (!searchResults) {
    return null;
  }

  return (
    <>
      <DialogContentText id="find-beer-dialog-description">
        Select a beer list from the search results:
      </DialogContentText>
      <Box
        sx={{
          mt: 2,
          mb: 1
        }}>
        <List>
          {searchResults?.organic?.map((result, index) => {
            // Use link as key since it should be unique, fallback to position or title+index
            // Note: link should always be present for valid search results
            // Include index in fallback to guarantee uniqueness when link/position are missing
            const resultKey = result.link || result.position?.toString() || `result-${result.title || 'unknown'}-${index}`;
            return (
              <React.Fragment key={resultKey}>
                <ListItem
                  disablePadding
                  sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    py: 1.5,
                  }}
                >
                  <ListItemText
                    primary={result.title || 'Untitled'}
                    secondary={
                      <>
                        {result.snippet && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              display: "block",
                              mb: 0.5
                            }}>
                            {result.snippet}
                          </Typography>
                        )}
                        {result.link && (
                          <Link
                            href={result.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onLinkClick}
                            sx={{
                              wordBreak: 'break-word',
                              fontSize: '0.875rem',
                            }}
                          >
                            {result.link}
                          </Link>
                        )}
                      </>
                    }
                    primaryTypographyProps={{
                      style: {
                        wordBreak: 'break-word',
                      },
                    }}
                    secondaryTypographyProps={{
                      component: 'div',
                      style: {
                        wordBreak: 'break-word',
                      },
                    }}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      pr: { xs: 0, sm: 2 },
                      mb: { xs: 1, sm: 0 },
                    }}
                  />
                  <Box
                    sx={{
                      alignSelf: { xs: 'flex-start', sm: 'center' },
                      mt: { xs: 0, sm: 0 },
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      data-url={result.link}
                      onClick={onSelectClick}
                      disabled={scrapingLoading || !result.link}
                      startIcon={
                        scrapingLoading && scrapingUrl === result.link ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {scrapingLoading && scrapingUrl === result.link
                        ? 'Scraping...'
                        : 'Use This'}
                    </Button>
                  </Box>
                </ListItem>
                {index < (searchResults?.organic?.length || 0) - 1 && (
                  <Divider />
                )}
              </React.Fragment>
            );
          })}
          {(!searchResults?.organic ||
            searchResults.organic.length === 0) && (
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              No search results found.
            </Typography>
          )}
        </List>
      </Box>
    </>
  );
}

SearchResultsList.propTypes = {
  searchResults: PropTypes.shape({
    organic: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        snippet: PropTypes.string,
        link: PropTypes.string,
        position: PropTypes.number,
      }),
    ),
  }),
  scrapingLoading: PropTypes.bool.isRequired,
  scrapingUrl: PropTypes.string,
  onSelectClick: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func.isRequired,
};

SearchResultsList.defaultProps = {
  searchResults: null,
  scrapingUrl: null,
};

export default SearchResultsList;
